import React, { useEffect } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Checkbox, Tooltip } from "@mui/material";
import { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { AbsCheckBox, Button, BUTTONS, Modal } from "abs-ui-components";
import "./CloneModal.scss"
import ListVesselTable from "./ListVesselTable";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#002a4e',
    color: '#ffffff',
    boxShadow: theme.shadows[1],
    fontSize: 13,
    padding: '6px 10px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#002a4e",
  },
}));

const getDependencies = (id, sections, visited = new Set()) => {
  if (visited.has(id)) return [];
  visited.add(id);

  const sec = sections.find(s => s.optionId === id);
  let result = [];

  sec?.relatedOptionIds?.forEach(rid => {
    result.push(rid);
    result = [...result, ...getDependencies(rid, sections, visited)];
  });

  return [...new Set(result)];
};

const CloneModal = ({
  isOpen,
  onClose,
  onClone,
  headerTitle = "Clone",
  sectionsList = [],
  vesselsList = [],
  selectedSections,
  setSelectedSections,
  selectedVessels,
  setSelectedVessels,
  confirmationChecked,
  setConfirmationChecked,
  planType,
  vesselMstId
}) => {

  return (
    <Modal
      isOpen={isOpen}
      padding="0"
      showFooter
      onCloseClickListener={onClose}
      footer={
        <div className="d-flex justify-content-between align-items-center mx-3 pt-0 mb-2 pl-1 footer-clone-border">
          <div className="text-muted mt-2">
            <p className="footer-clone-text">{selectedSections.length} sections · {selectedVessels.length} vessel(s) selected </p>
          </div>
          <div className="d-flex justify-content-end p-1">
            <Button className="ml-3 modal-footer-btn" onClick={onClose} variant={BUTTONS.SECONDARY}>Cancel</Button>
            <Button
              className="ms-2 ml-3 modal-footer-btn"
              disabled={
                !confirmationChecked ||
                !selectedSections.length ||
                !selectedVessels.length
              }
              onClick={onClone}
            >
              Clone
            </Button>
          </div>
        </div>
      }
    >
      <div className="clone-modal-header"
        style={{
          backgroundColor: "#00395D",
          color: "white",
          padding: "14px 20px",
          fontSize: "18px",
          fontWeight: "600",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          marginTop: "-1rem",
        }}
      >
        {headerTitle}
        <CloseIcon
          onClick={onClose}
          style={{
            cursor: "pointer",
            color: "white",
            border: "1px solid rgba(255, 255, 255, 0.6)",
            borderRadius: "50%",
            padding: "4px",
            boxSizing: "border-box",
          }}
        />
      </div>
      <div className="clone-modal" style={{ padding: "20px" }}>
        <Accordion className="custom-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="accordion-header-text">
              <strong style={{ fontWeight: "900" }}>Section 1. Select Sections to Clone</strong>
            </div>

            <div className="accordion-count">
              {selectedSections.length} selected
            </div>
          </AccordionSummary>
          <AccordionDetails>
            {sectionsList?.map((sec) => {
              const isDisabled = (planType?.toLowerCase() === "seemp ii" || planType?.toLowerCase() === "seemp iii") && sec.optionId === 2;
              return (
                <LightTooltip
                  title={sec.helperSection}
                  placement="top"
                  arrow
                >
                  <div key={sec.optionId}
                    className=" mb-2 section-item"
                    style={{
                      pointerEvents: isDisabled ? "none" : "auto",
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    <AbsCheckBox
                      slim={true}
                      id={`section_${sec.optionId}`}
                      checked={selectedSections?.includes(sec.optionId)}
                      disabled={isDisabled}
                      handleChange={(e) => {
                        const isChecked = e.target.checked;
                        let updated = [...selectedSections];

                        const findSec = (id) => sectionsList.find(s => s.optionId === id);

                        if (isChecked) {
                          if (!updated.includes(sec.optionId)) updated.push(sec.optionId);

                          const deps = getDependencies(sec.optionId, sectionsList);
                          deps.forEach(d => {
                            if (!updated.includes(d)) updated.push(d);
                          });

                        } else {
                          updated = updated.filter(id => id !== sec.optionId);

                          const directDeps = sec.relatedOptionIds ?? [];

                          directDeps.forEach(dep => {
                            if (!updated.includes(dep)) return;

                            const depSec = findSec(dep);

                            const isMutual = depSec?.relatedOptionIds?.includes(sec.optionId);

                            if (isMutual) {
                              updated = updated.filter(x => x !== dep);

                              const subDeps = getDependencies(dep, sectionsList);
                              subDeps.forEach(sd => {
                                const stillRequired = sectionsList.some(s =>
                                  updated.includes(s.optionId) && s.relatedOptionIds?.includes(sd)
                                );
                                if (!stillRequired) updated = updated.filter(x => x !== sd);
                              });

                              return;
                            }
                            if (directDeps.length > 1) {
                              return;
                            }
                            const stillRequiredByOther = sectionsList.some(s =>
                              s.optionId !== sec.optionId &&
                              updated.includes(s.optionId) &&
                              s.relatedOptionIds?.includes(dep)
                            );

                            if (!stillRequiredByOther) {
                              updated = updated.filter(x => x !== dep);

                              const subDeps = getDependencies(dep, sectionsList);
                              subDeps.forEach(sd => {
                                const stillRequired = sectionsList.some(s =>
                                  updated.includes(s.optionId) && s.relatedOptionIds?.includes(sd)
                                );
                                if (!stillRequired) updated = updated.filter(x => x !== sd);
                              });
                            }
                          });
                          const allSelected = new Set(updated);
                          sectionsList.forEach(s => {
                            if (!allSelected.has(s.optionId)) return;
                            (s.relatedOptionIds ?? []).forEach(dep => {
                              if (!allSelected.has(dep)) {
                              }
                            });
                          });
                        }
                        setSelectedSections([...new Set(updated)]);
                      }}
                    />
                    <label className="ms-2 accordion-header-text" htmlFor={`section_${sec.optionId}`} style={{ cursor: "pointer" }}>{sec.optionId}. {sec.section}</label>
                    <label className="ms-2 text-muted" style={{ fontSize: "12px", marginLeft: "40px", fontWeight: "800" }}>{sec.helperSection}</label>
                  </div>
                </LightTooltip>)
            }
            )}
            {
              sectionsList.length != 0 ?
                <div>
                  <div className="d-flex justify-content-start pt-2">
                    <Button
                      className="ms-2 modal-footer-btn"
                      onClick={() => {
                        const allIds = sectionsList?.map((s) => s.optionId).filter((id) => {
                          if ((planType?.toLowerCase() === "seemp ii" || planType?.toLowerCase() === "seemp iii") && id === 2) {
                            return false;
                          }
                          return true;
                        });
                        setSelectedSections(allIds);
                      }}
                    >
                      Select All
                    </Button>
                    <Button className="ml-3 modal-footer-btn" variant={BUTTONS.SECONDARY} onClick={() => setSelectedSections([])}>Clear All</Button>
                  </div>
                </div>
                : <></>
            }
          </AccordionDetails>
        </Accordion>
        <Accordion className="custom-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="accordion-header-text">
              <strong style={{ fontWeight: "900" }}>Section 2. Select Target Vessels</strong>
            </div>
            <div className="accordion-count">{selectedVessels.length} selected</div>
          </AccordionSummary>

          <AccordionDetails>
            <ListVesselTable
              selectedVessels={selectedVessels}
              setSelectedVessels={setSelectedVessels}
              vesselMstId={vesselMstId}
            />
          </AccordionDetails>
          <AccordionDetails>
            {vesselsList?.map((v) => (
              <div key={v.id} className="mb-2">
                <input
                  type="checkbox"

                  checked={selectedVessels.includes(v.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedVessels([...selectedVessels, v.id]);
                    } else {
                      setSelectedVessels(
                        selectedVessels.filter((s) => s !== v.id)
                      );
                    }
                  }}
                />
                <label className="ms-2">{v.name}</label>
              </div>
            ))}
          </AccordionDetails>
        </Accordion>

        <div className="mt-4">
          <h5 className="confirm-text mb-2">Confirmation</h5>

          <div className="d-flex justify-content-start mt-2 acknowledge-checkbox">
            <AbsCheckBox
              className=""
              slim={true}
              id="confirmation_checkbox"
              checked={confirmationChecked}
              handleChange={(e) => { setConfirmationChecked(e.target.checked) }}
            />
            <label className="ms-2" style={{ cursor: "pointer", fontSize: "15px" }} onClick={() => setConfirmationChecked(!confirmationChecked)}>
              I acknowledge that the clone action will overwrite the selected Monitoring
              Plan for the selected vessels.
            </label>
          </div>

        </div>
      </div>
    </Modal>
  );
};

export default CloneModal;
